'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { Pencil, ChevronDown, ChevronUp, Trash2, ChevronRight, UtensilsCrossed } from 'lucide-react'
import MockupChrome from './MockupChrome'
import '../../sass/landing/MenuEditorMockup.scss'

interface MockupAllergen {
  key: string
  color: string
}

interface MockupItem {
  itemKey: 'ensaladillaRusa' | 'tablaQuesos' | 'papasBravas' | 'croquetasCaseras'
  allergens: MockupAllergen[]
  image: string
}

interface MockupSection {
  id: 'entrantes' | 'bebidas'
  open: boolean
  items?: MockupItem[]
}

const openItems: MockupItem[] = [
  {
    itemKey: 'ensaladillaRusa',
    allergens: [
      { key: 'huevos', color: '#f5a524' },
      { key: 'lacteo', color: '#a8b0c0' },
      { key: 'pescado', color: '#4a9ce0' }
    ],
    image: '/img/landing/food/crema.jpg'
  },
  {
    itemKey: 'tablaQuesos',
    allergens: [
      { key: 'lacteo', color: '#f5a524' },
      { key: 'frutos con cáscara', color: '#e8843b' },
      { key: 'pescado', color: '#4a9ce0' }
    ],
    image: '/img/landing/food/quesos.jpg'
  },
  {
    itemKey: 'papasBravas',
    allergens: [
      { key: 'pescado', color: '#4a9ce0' },
      { key: 'gluten', color: '#a8b0c0' }
    ],
    image: '/img/landing/food/bravas.jpg'
  },
  {
    itemKey: 'croquetasCaseras',
    allergens: [
      { key: 'gluten', color: '#f5a524' },
      { key: 'frutos con cáscara', color: '#4a9ce0' }
    ],
    image: '/img/landing/food/tacos.jpg'
  }
]

const sections: MockupSection[] = [
  { id: 'entrantes', open: true, items: openItems },
  { id: 'bebidas', open: false }
]

const MenuEditorMockup = () => {
  const t = useTranslations('LandingV2.mockups.menu')

  return (
    <div className='menu-editor' aria-label={t('ariaLabel')}>
      <MockupChrome
        icon={<UtensilsCrossed size={11} strokeWidth={2.4} />}
        address={t('address')}
      />

      <div className='menu-editor__body'>
        {sections.map((section) => (
          <section key={section.id} className='menu-editor__section'>
            <SectionHead title={t(`sections.${section.id}`)} open={section.open} />
            {section.open && section.items && (
              <div className='menu-editor__section-body'>
                <div className='menu-editor__table-wrap'>
                  <div className='menu-editor__table'>
                    <div className='menu-editor__table-head'>
                      <span className='menu-editor__col menu-editor__col--name'>{t('columns.name')}</span>
                      <span className='menu-editor__col menu-editor__col--desc'>{t('columns.description')}</span>
                      <span className='menu-editor__col menu-editor__col--price'>{t('columns.price')}</span>
                      <span className='menu-editor__col menu-editor__col--allergens'>{t('columns.allergens')}</span>
                      <span className='menu-editor__col menu-editor__col--image'>{t('columns.image')}</span>
                    </div>

                    {section.items.map((item) => (
                      <div key={item.itemKey} className='menu-editor__row'>
                        <span className='menu-editor__cell menu-editor__cell--name'>{t(`items.${item.itemKey}.name`)}</span>
                        <span className='menu-editor__cell menu-editor__cell--desc'>{t(`items.${item.itemKey}.description`)}</span>
                        <span className='menu-editor__cell menu-editor__cell--price'>{t(`items.${item.itemKey}.price`)}€</span>
                        <span className='menu-editor__cell menu-editor__cell--allergens'>
                          <span className='menu-editor__allergens'>
                            {item.allergens.map((a, i) => (
                              <span
                                key={`${item.itemKey}-${i}`}
                                className='menu-editor__allergen'
                                style={{ background: a.color }}
                              >
                                <Image
                                  src={`/img/allergens/${a.key}.svg`}
                                  alt=''
                                  width={11}
                                  height={11}
                                  aria-hidden='true'
                                />
                              </span>
                            ))}
                          </span>
                        </span>
                        <span className='menu-editor__cell menu-editor__cell--image'>
                          <span className='menu-editor__thumb'>
                            <Image
                              src={item.image}
                              alt=''
                              width={32}
                              height={32}
                              aria-hidden='true'
                            />
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </section>
        ))}

        <button type='button' tabIndex={-1} className='menu-editor__add-section'>
          <span>{t('addSection')}</span>
          <ChevronRight size={16} strokeWidth={2.6} />
        </button>
      </div>
    </div>
  )
}

interface SectionHeadProps {
  title: string
  open: boolean
}

const SectionHead = ({ title, open }: SectionHeadProps) => {
  const t = useTranslations('LandingV2.mockups.menu')

  return (
    <header className='menu-editor__section-head'>
      <h3 className='menu-editor__section-title'>
        {title}
        <Pencil size={11} strokeWidth={2.2} className='menu-editor__edit-icon' aria-hidden='true' />
      </h3>
      <div className='menu-editor__section-actions'>
        <span className='menu-editor__toggle menu-editor__toggle--on' aria-hidden='true'>
          <span className='menu-editor__toggle-knob' />
        </span>
        <button type='button' tabIndex={-1} className='menu-editor__icon-btn' aria-label={t('deleteAria')}>
          <Trash2 size={12} strokeWidth={2.2} />
        </button>
        <span className='menu-editor__chevron' aria-hidden='true'>
          {open
            ? <ChevronUp size={14} strokeWidth={2.6} />
            : <ChevronDown size={14} strokeWidth={2.6} />}
        </span>
      </div>
    </header>
  )
}

export default MenuEditorMockup
